import { TEE_COLORS } from '@app/components/molecules/TeeColorPicker/CustomColorInput'

export type TeeSetKind = 'standard' | 'hectometer'

export type TeeTemplate = {
  title: string
  color: string
}

// Hectometer tees are not set out by colour, so they are all white, and a tee
// that comes with no colour of its own starts there too.
export const DEFAULT_TEE_COLOR = '#ffffff'

const colorOf = (name: string) =>
  TEE_COLORS.find((teeColor) => teeColor.name === name)?.color ??
  DEFAULT_TEE_COLOR

// The colours a course sets its tees out in, from the back of the course to
// the front. Which set is used follows from how many tees the course has.
export const STANDARD_TEE_SETS: { [count: number]: string[] } = {
  2: ['Gul', 'Röd'],
  4: ['Vit', 'Gul', 'Blå', 'Röd'],
  5: ['Vit', 'Gul', 'Blå', 'Röd', 'Orange'],
  6: ['Svart', 'Vit', 'Gul', 'Blå', 'Röd', 'Orange'],
}

export const TEE_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10]

export const TEE_SET_KINDS: {
  value: TeeSetKind
  label: string
  hint: string
}[] = [
  {
    value: 'standard',
    label: 'Standard tees',
    hint: 'Named and coloured as the course is set out',
  },
  {
    value: 'hectometer',
    label: 'Hectometer tees',
    hint: 'No colours, white throughout',
  },
]

/**
 * Whether the standard set names and colours this many tees. A count it does
 * not cover is named by hand, so nothing carries over into it.
 */
export const hasStandardSet = (kind: TeeSetKind, count: number) =>
  kind === 'standard' && !!STANDARD_TEE_SETS[count]

const blankTees = (count: number): TeeTemplate[] =>
  Array.from({ length: count }, () => ({
    title: '',
    color: DEFAULT_TEE_COLOR,
  }))

export const teeTemplates = (
  kind: TeeSetKind,
  count: number
): TeeTemplate[] => {
  if (kind === 'hectometer') return blankTees(count)
  const set = STANDARD_TEE_SETS[count]
  // A course with a number of tees that has no set of its own is left to fill
  // the names in rather than given a set that was guessed at.
  if (!set) return blankTees(count)
  return set.map((title) => ({ title, color: colorOf(title) }))
}
